import { useState, useRef } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

import { VisxText } from '@/components/VisxText';

import { useAuthorProfileImage } from '../../hooks';
import { getProfileDx } from './utils';

import { Image } from './styled';

interface Props {
  x: number;
  y: number | string;
  label?: string;
  authorProfileImage?: string;
  value: string;
  bound: number;
  height: number;
}

const Label = ({
  x,
  y,
  label,
  value,
  bound,
  height,
  authorProfileImage,
}: Props) => {
  const theme = useTheme();
  const containerRef = useRef<SVGSVGElement>(null);
  const [labelRight, setLabelRight] = useState(false);
  useIsomorphicLayoutEffect(() => {
    const { current: container } = containerRef;
    if (container !== null && bound > 0) {
      const width = container.getBBox().width + theme.x4spacePx;
      const containerWidthPercentage = width / bound;
      const nextLabelRight = x < containerWidthPercentage;
      if (labelRight !== nextLabelRight) {
        setLabelRight(nextLabelRight);
      }
    }
  }, [x, bound, labelRight]);

  const authorImageUrl = useAuthorProfileImage({
    src: authorProfileImage ?? null,
    width: 24,
    height: 24,
  });

  return (
    <svg
      x={getProfileDx(x, labelRight, containerRef.current)}
      y={y}
      height={height}
      width="100%"
      ref={containerRef}
    >
      {authorProfileImage && (
        <foreignObject
          y={height / 2 - 12}
          x={theme.x1spacePx}
          width={theme.x3spacePx}
          height={theme.x3spacePx}
        >
          <Image alt="Author Valuation" src={authorImageUrl} />
        </foreignObject>
      )}
      <VisxText
        data-cy-id={`bar-chart-label-${label}`}
        verticalAnchor="end"
        y={height / 2 + 4}
        x={authorProfileImage ? theme.x5spacePx : theme.x1spacePx}
        fill={theme.color.white}
        typography="xSmall"
      >
        {!authorProfileImage ? `${label} ${value}` : value}
      </VisxText>
    </svg>
  );
};

export default Label;
